import React, { useState, useRef, useEffect } from 'react';
import { Stage, Layer, Rect, Circle, Text, Group, Transformer } from 'react-konva';
import { 
  Table, 
  Armchair, 
  Music, 
  MapPin, 
  DoorOpen, 
  DoorClosed, 
  Utensils,
  Type,
  Download,
  Save,
  Upload,
  RotateCcw,
  Grid,
  ZoomIn,
  ZoomOut,
  Move,
  Trash2,
  Settings,
  Palette,
  RotateCw
} from 'lucide-react';
import Konva from 'konva';
import { saveLayoutToRegistry, generateLayoutExport } from '../lib/eventPlanner';
import type { EventElement, GuestInfo, LayoutState, TableElement } from '../types/eventPlanner';

interface Props {
  registryId: string;
  guests: GuestInfo[];
  onLayoutChange: (layout: any) => void;
}

export default function EventLayoutPlanner({ registryId, guests, onLayoutChange }: Props) {
  const stageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const [isStageReady, setIsStageReady] = useState(false);
  const [loadingLayout, setLoadingLayout] = useState(true);
  const [savingLayout, setSavingLayout] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  
  const [layout, setLayout] = useState<LayoutState>({
    elements: [],
    selectedElementId: null,
    canvasWidth: 800,
    canvasHeight: 600,
    zoom: 1,
    gridSize: 20,
    showGrid: true
  });

  const [selectedElement, setSelectedElement] = useState<EventElement | null>(null);
  const [editingText, setEditingText] = useState<{ elementId: string; text: string } | null>(null);

  // Element templates with proper defaults
  const elementTemplates = {
    table: { width: 80, height: 80, color: '#8B4513' },
    chair: { width: 30, height: 30, color: '#654321' },
    stage: { width: 120, height: 80, color: '#4A5568' },
    bar: { width: 100, height: 40, color: '#2D3748' },
    dj_booth: { width: 60, height: 60, color: '#1A202C' },
    entrance: { width: 60, height: 20, color: '#38A169' },
    exit: { width: 60, height: 20, color: '#E53E3E' },
    buffet: { width: 120, height: 60, color: '#FF8C00' },
    text_label: { width: 100, height: 30, color: '#000000' }
  };

  useEffect(() => {
    console.log('EventLayoutPlanner - Component mounting with registryId:', registryId);
    // Mark stage as ready after component mounts
    const timer = setTimeout(() => {
      console.log('EventLayoutPlanner - Setting stage as ready');
      setIsStageReady(true);
      loadSavedLayout();
    }, 100);
    return () => clearTimeout(timer);
  }, [registryId]);

  useEffect(() => {
    if (selectedElement && transformerRef.current) {
      const stage = stageRef.current;
      if (stage) {
        const node = stage.findOne(`#${selectedElement.id}`);
        if (node) {
          transformerRef.current.nodes([node]);
          transformerRef.current.getLayer()?.batchDraw();
        }
      }
    }
  }, [selectedElement]);

  // Debounced save function
  useEffect(() => {
    if (layout.elements.length > 0 && isStageReady) {
      const saveTimer = setTimeout(() => {
        console.log('EventLayoutPlanner - Auto-saving layout with elements:', layout.elements.length);
        saveLayoutToDatabase();
      }, 1000); // Save after 1 second of inactivity

      return () => clearTimeout(saveTimer);
    }
  }, [layout.elements, isStageReady]);

  const loadSavedLayout = async () => {
    try {
      console.log('EventLayoutPlanner - Loading saved layout for registryId:', registryId);
      setLoadingLayout(true);
      
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase
        .from('registries')
        .select('theme')
        .eq('id', registryId)
        .single();

      console.log('EventLayoutPlanner - Raw data from Supabase:', data);
      console.log('EventLayoutPlanner - Error from Supabase:', error);

      if (error) {
        console.error('EventLayoutPlanner - Error loading layout:', error);
        throw error;
      }

      if (data?.theme?.layout) {
        console.log('EventLayoutPlanner - Found saved layout with elements:', data.theme.layout.length);
        setLayout(prev => ({
          ...prev,
          elements: data.theme.layout
        }));
      } else {
        console.log('EventLayoutPlanner - No saved layout found, starting with empty layout');
      }
    } catch (error) {
      console.error('EventLayoutPlanner - Error loading saved layout:', error);
    } finally {
      setLoadingLayout(false);
    }
  };

  const saveLayoutToDatabase = async () => {
    try {
      console.log('EventLayoutPlanner - Starting save operation');
      console.log('EventLayoutPlanner - Elements to save:', layout.elements);
      console.log('EventLayoutPlanner - Registry ID:', registryId);
      
      setSavingLayout(true);
      setSaveStatus('saving');

      await saveLayoutToRegistry(registryId, {
        elements: layout.elements,
        metadata: {
          canvasWidth: layout.canvasWidth,
          canvasHeight: layout.canvasHeight,
          lastModified: new Date().toISOString()
        }
      });

      console.log('EventLayoutPlanner - Save operation completed successfully');
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('EventLayoutPlanner - Error saving layout:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSavingLayout(false);
    }
  };

  const quickAddElement = (type: EventElement['type']) => {
    console.log('EventLayoutPlanner - quickAddElement called with type:', type);
    console.log('EventLayoutPlanner - isStageReady:', isStageReady);
    console.log('EventLayoutPlanner - stageRef.current:', stageRef.current);
    
    // Early return if stage is not ready
    if (!isStageReady || !stageRef.current) {
      console.warn('EventLayoutPlanner - Stage not ready for element addition');
      return;
    }

    const stage = stageRef.current;
    const container = stage.container();
    
    if (!container) {
      console.warn('EventLayoutPlanner - Stage container not available');
      return;
    }

    const rect = container.getBoundingClientRect();
    
    if (!rect || typeof rect.width !== 'number' || typeof rect.height !== 'number') {
      console.warn('EventLayoutPlanner - Invalid container dimensions');
      return;
    }

    const template = elementTemplates[type];
    if (!template) {
      console.warn(`EventLayoutPlanner - No template found for element type: ${type}`);
      return;
    }

    console.log('EventLayoutPlanner - Creating element with template:', template);

    const newElement: EventElement = {
      id: `${type}_${Date.now()}`,
      type,
      x: rect.width / 2 - template.width / 2,
      y: rect.height / 2 - template.height / 2,
      width: template.width,
      height: template.height,
      rotation: 0,
      color: template.color,
      properties: getDefaultProperties(type),
      text: type === 'text_label' ? 'Text Label' : undefined
    };

    console.log('EventLayoutPlanner - New element created:', newElement);

    setLayout(prev => {
      const newLayout = {
        ...prev,
        elements: [...prev.elements, newElement],
        selectedElementId: newElement.id
      };
      console.log('EventLayoutPlanner - Updated layout state:', newLayout);
      return newLayout;
    });

    setSelectedElement(newElement);
  };

  const getDefaultProperties = (type: EventElement['type']) => {
    switch (type) {
      case 'table':
        return { 
          shape: 'round', 
          capacity: 8, 
          tableNumber: layout.elements.filter(e => e.type === 'table').length + 1, 
          tableName: ''
        };
      case 'chair':
        return { assignedGuest: '', tableId: '' };
      case 'stage':
        return { name: 'Main Stage', equipment: [] };
      case 'buffet':
        return { name: 'Buffet Station', type: 'Main Course' };
      case 'text_label':
        return { fontSize: 16, fontFamily: 'Arial' };
      default:
        return {};
    }
  };

  const handleElementClick = (element: EventElement, e: any) => {
    e.evt.preventDefault();
    e.evt.stopPropagation();
    
    setSelectedElement(element);
    setLayout(prev => ({
      ...prev,
      selectedElementId: element.id
    }));
  };

  const handleElementDoubleClick = (element: EventElement, e: any) => {
    e.evt.preventDefault();
    e.evt.stopPropagation();
    
    if (element.type === 'text_label') {
      setEditingText({
        elementId: element.id,
        text: element.text || 'Text Label'
      });
    }
  };

  const handleStageClick = (e: any) => {
    e.evt.preventDefault();
    
    if (e.target === e.target.getStage()) {
      setSelectedElement(null);
      setLayout(prev => ({
        ...prev,
        selectedElementId: null
      }));
      if (transformerRef.current) {
        transformerRef.current.nodes([]);
      }
    }
  };

  const handleElementDrag = (elementId: string, newPos: { x: number; y: number }) => {
    setLayout(prev => ({
      ...prev,
      elements: prev.elements.map(el =>
        el.id === elementId ? { ...el, x: newPos.x, y: newPos.y } : el
      )
    }));

    const updatedElement = layout.elements.find(el => el.id === elementId);
    if (updatedElement) {
      setSelectedElement({ ...updatedElement, x: newPos.x, y: newPos.y });
    }
  };

  const handleElementTransform = (elementId: string, newAttrs: any) => {
    setLayout(prev => ({
      ...prev,
      elements: prev.elements.map(el =>
        el.id === elementId 
          ? { 
              ...el, 
              x: newAttrs.x,
              y: newAttrs.y,
              width: Math.max(20, newAttrs.width),
              height: Math.max(20, newAttrs.height),
              rotation: newAttrs.rotation
            } 
          : el
      )
    }));

    const updatedElement = layout.elements.find(el => el.id === elementId);
    if (updatedElement) {
      setSelectedElement({
        ...updatedElement,
        x: newAttrs.x,
        y: newAttrs.y,
        width: Math.max(20, newAttrs.width),
        height: Math.max(20, newAttrs.height),
        rotation: newAttrs.rotation
      });
    }
  };

  const updateElementProperty = (property: string, value: any) => {
    if (!selectedElement) return;

    const updatedElement = { ...selectedElement, [property]: value };
    
    if (property === 'properties') {
      updatedElement.properties = { ...selectedElement.properties, ...value };
    }

    setLayout(prev => ({
      ...prev,
      elements: prev.elements.map(el =>
        el.id === selectedElement.id ? updatedElement : el
      )
    }));

    setSelectedElement(updatedElement);
  };

  const deleteElement = () => {
    if (!selectedElement) return;

    setLayout(prev => ({
      ...prev,
      elements: prev.elements.filter(el => el.id !== selectedElement.id),
      selectedElementId: null
    }));

    setSelectedElement(null);
    if (transformerRef.current) {
      transformerRef.current.nodes([]);
    }
  };

  const exportLayout = () => {
    const exportData = generateLayoutExport(layout.elements, guests, {
      registryId,
      canvasWidth: layout.canvasWidth,
      canvasHeight: layout.canvasHeight,
      exportedAt: new Date().toISOString()
    });

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `event-layout-${registryId}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportAsImage = () => {
    if (!stageRef.current) return;

    const stage = stageRef.current;
    const dataURL = stage.toDataURL({
      mimeType: 'image/png',
      quality: 1,
      pixelRatio: 2 // Higher quality
    });

    const link = document.createElement('a');
    link.href = dataURL;
    link.download = `event-layout-${registryId || 'layout'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const saveTextEdit = () => {
    if (!editingText) return;

    setLayout(prev => ({
      ...prev,
      elements: prev.elements.map(el =>
        el.id === editingText.elementId 
          ? { ...el, text: editingText.text }
          : el
      )
    }));

    const updatedElement = layout.elements.find(el => el.id === editingText.elementId);
    if (updatedElement) {
      setSelectedElement({ ...updatedElement, text: editingText.text });
    }

    setEditingText(null);
  };

  const cancelTextEdit = () => {
    setEditingText(null);
  };

  const handleTextEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveTextEdit();
    } else if (e.key === 'Escape') {
      cancelTextEdit();
    }
  };

  const renderElement = (element: EventElement) => {
    const commonProps = {
      id: element.id,
      draggable: true,
      onDragEnd: (e: any) => {
        e.evt.preventDefault();
        e.evt.stopPropagation();
        handleElementDrag(element.id, { x: e.target.x(), y: e.target.y() });
      },
      onTransformEnd: (e: any) => {
        e.evt.preventDefault();
        e.evt.stopPropagation();
        const node = e.target;
        handleElementTransform(element.id, {
          x: node.x(),
          y: node.y(),
          width: node.width() * node.scaleX(),
          height: node.height() * node.scaleY(),
          rotation: node.rotation()
        });
        node.scaleX(1);
        node.scaleY(1);
      },
      onClick: (e: any) => handleElementClick(element, e),
      onTap: (e: any) => handleElementClick(element, e),
      onDblClick: (e: any) => handleElementDoubleClick(element, e),
      onMouseDown: (e: any) => {
        e.evt.preventDefault();
        e.evt.stopPropagation();
      }
    };

    // For tables and buffets, use Group to ensure text rotates with the element
    if (element.type === 'table' || element.type === 'buffet') {
      const tableElement = element as TableElement;
      
      return (
        <Group
          key={element.id}
          x={element.x}
          y={element.y}
          width={element.width}
          height={element.height}
          rotation={element.rotation}
          {...commonProps}
        >
          {element.type === 'table' && tableElement.properties.shape === 'round' ? (
            <Circle
              x={element.width / 2}
              y={element.height / 2}
              radius={element.width / 2}
              fill={element.color}
              stroke="#000"
              strokeWidth={2}
            />
          ) : (
            <Rect
              x={0}
              y={0}
              width={element.width}
              height={element.height}
              fill={element.color}
              stroke="#000"
              strokeWidth={2}
            />
          )}
          <Text
            x={0}
            y={0}
            width={element.width}
            height={element.height}
            text={element.type === 'table' 
              ? `T${tableElement.properties.tableNumber || 1}` 
              : element.properties.name || 'Buffet'
            }
            fontSize={14}
            fontFamily="Arial"
            fill="white"
            align="center"
            verticalAlign="middle"
          />
        </Group>
      );
    }

    // For text labels, apply rotation directly
    if (element.type === 'text_label') {
      return (
        <Text
          key={element.id}
          x={element.x}
          y={element.y}
          width={element.width}
          height={element.height}
          rotation={element.rotation}
          text={element.text || 'Text Label'}
          fontSize={element.properties.fontSize || 16}
          fontFamily={element.properties.fontFamily || 'Arial'}
          fill={element.color}
          align="center"
          verticalAlign="middle"
          {...commonProps}
        />
      );
    }

    // For other elements without text
    return (
      <Rect
        key={element.id}
        x={element.x}
        y={element.y}
        width={element.width}
        height={element.height}
        rotation={element.rotation}
        fill={element.color}
        stroke="#000"
        strokeWidth={2}
        {...commonProps}
      />
    );
  };

  if (loadingLayout) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-3 text-gray-600">Loading layout...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="card-pastel backdrop-blur-sm rounded-xl p-4">
        <div className="flex flex-wrap gap-2 mb-4">
          <h3 className="text-lg font-medium text-gray-800 w-full mb-2 font-poppins">Add Elements</h3>
          
          <button
            onClick={() => quickAddElement('table')}
            disabled={!isStageReady}
            className="flex items-center space-x-2 px-3 py-2 bg-pastel-blue-400 text-white rounded-md hover:bg-pastel-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Table className="w-4 h-4" />
            <span>Table</span>
          </button>
          
          <button
            onClick={() => quickAddElement('chair')}
            disabled={!isStageReady}
            className="flex items-center space-x-2 px-3 py-2 bg-pastel-green-400 text-white rounded-md hover:bg-pastel-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Armchair className="w-4 h-4" />
            <span>Chair</span>
          </button>
          
          <button
            onClick={() => quickAddElement('stage')}
            disabled={!isStageReady}
            className="flex items-center space-x-2 px-3 py-2 bg-pastel-purple-400 text-white rounded-md hover:bg-pastel-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Music className="w-4 h-4" />
            <span>Stage</span>
          </button>
          
          <button
            onClick={() => quickAddElement('bar')}
            disabled={!isStageReady}
            className="flex items-center space-x-2 px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MapPin className="w-4 h-4" />
            <span>Bar</span>
          </button>
          
          <button
            onClick={() => quickAddElement('dj_booth')}
            disabled={!isStageReady}
            className="flex items-center space-x-2 px-3 py-2 bg-indigo-400 text-white rounded-md hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Music className="w-4 h-4" />
            <span>DJ Booth</span>
          </button>
          
          <button
            onClick={() => quickAddElement('entrance')}
            disabled={!isStageReady}
            className="flex items-center space-x-2 px-3 py-2 bg-pastel-green-400 text-white rounded-md hover:bg-pastel-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DoorOpen className="w-4 h-4" />
            <span>Entrance</span>
          </button>
          
          <button
            onClick={() => quickAddElement('exit')}
            disabled={!isStageReady}
            className="flex items-center space-x-2 px-3 py-2 bg-red-400 text-white rounded-md hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DoorClosed className="w-4 h-4" />
            <span>Exit</span>
          </button>
          
          <button
            onClick={() => quickAddElement('buffet')}
            disabled={!isStageReady}
            className="flex items-center space-x-2 px-3 py-2 bg-orange-400 text-white rounded-md hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Utensils className="w-4 h-4" />
            <span>Buffet</span>
          </button>
          
          <button
            onClick={() => quickAddElement('text_label')}
            disabled={!isStageReady}
            className="flex items-center space-x-2 px-3 py-2 bg-yellow-400 text-white rounded-md hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Type className="w-4 h-4" />
            <span>Text Label</span>
          </button>
        </div>

        {/* Canvas Controls */}
        <div className="flex items-center justify-between pt-4 border-t border-pastel-purple-200">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={layout.showGrid}
                onChange={(e) => setLayout(prev => ({ ...prev, showGrid: e.target.checked }))}
                className="rounded"
              />
              <label className="text-gray-700 text-sm">Show Grid</label>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setLayout(prev => ({ ...prev, zoom: Math.max(0.5, prev.zoom - 0.1) }))}
                className="p-2 bg-white/60 text-gray-700 rounded hover:bg-white/80"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-gray-700 text-sm">{Math.round(layout.zoom * 100)}%</span>
              <button
                onClick={() => setLayout(prev => ({ ...prev, zoom: Math.min(2, prev.zoom + 0.1) }))}
                className="p-2 bg-white/60 text-gray-700 rounded hover:bg-white/80"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={exportLayout}
              className="flex items-center space-x-2 px-4 py-2 bg-pastel-green-400 text-white rounded-md hover:bg-pastel-green-500"
            >
              <Download className="w-4 h-4" />
              <span>Export JSON</span>
            </button>
            
            <button
              onClick={exportAsImage}
              className="flex items-center space-x-2 px-4 py-2 bg-pastel-purple-400 text-white rounded-md hover:bg-pastel-purple-500"
            >
              <Download className="w-4 h-4" />
              <span>Export Image</span>
            </button>
            
            <button
              onClick={saveLayoutToDatabase}
              disabled={savingLayout}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                saveStatus === 'saved'
                  ? 'bg-pastel-green-500 text-white'
                  : saveStatus === 'error'
                  ? 'bg-red-500 text-white'
                  : 'btn-pastel text-white'
              }`}
            >
              <Save className="w-4 h-4" />
              <span>
                {saveStatus === 'saving'
                  ? 'Saving...'
                  : saveStatus === 'saved'
                  ? 'Saved!'
                  : saveStatus === 'error'
                  ? 'Error!'
                  : 'Save Layout'}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Canvas */}
        <div className="lg:col-span-3">
          <div className="card-pastel rounded-xl p-4">
            <div className="bg-white rounded-lg overflow-hidden" style={{ width: '100%', height: '600px' }}>
              <Stage
                ref={stageRef}
                width={layout.canvasWidth}
                height={layout.canvasHeight}
                scaleX={layout.zoom}
                scaleY={layout.zoom}
                onClick={handleStageClick}
                onTap={handleStageClick}
              >
                <Layer>
                  {/* Grid */}
                  {layout.showGrid && (
                    <>
                      {Array.from({ length: Math.ceil(layout.canvasWidth / layout.gridSize) + 1 }).map((_, i) => (
                        <Rect
                          key={`grid-v-${i}`}
                          x={i * layout.gridSize}
                          y={0}
                          width={1}
                          height={layout.canvasHeight}
                          fill="#e0e0e0"
                        />
                      ))}
                      {Array.from({ length: Math.ceil(layout.canvasHeight / layout.gridSize) + 1 }).map((_, i) => (
                        <Rect
                          key={`grid-h-${i}`}
                          x={0}
                          y={i * layout.gridSize}
                          width={layout.canvasWidth}
                          height={1}
                          fill="#e0e0e0"
                        />
                      ))}
                    </>
                  )}

                  {/* Elements */}
                  {layout.elements.map(renderElement)}

                  {/* Transformer */}
                  <Transformer
                    ref={transformerRef}
                    boundBoxFunc={(oldBox, newBox) => {
                      if (newBox.width < 20 || newBox.height < 20) {
                        return oldBox;
                      }
                      return newBox;
                    }}
                  />
                </Layer>
              </Stage>
            </div>
          </div>
        </div>

        {/* Properties Panel */}
        <div className="card-pastel backdrop-blur-sm rounded-xl p-4 space-y-4">
          <h3 className="text-lg font-medium text-gray-800 font-poppins">Properties</h3>
          
          {selectedElement ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Element Type
                </label>
                <p className="text-gray-800 capitalize">{selectedElement.type.replace('_', ' ')}</p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Width
                  </label>
                  <input
                    type="number"
                    value={Math.round(selectedElement.width)}
                    onChange={(e) => updateElementProperty('width', Math.max(20, Number(e.target.value)))}
                    className="w-full px-2 py-1 bg-white/60 border border-white/40 text-gray-800 rounded text-sm"
                    min="20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Height
                  </label>
                  <input
                    type="number"
                    value={Math.round(selectedElement.height)}
                    onChange={(e) => updateElementProperty('height', Math.max(20, Number(e.target.value)))}
                    className="w-full px-2 py-1 bg-white/60 border border-white/40 text-gray-800 rounded text-sm"
                    min="20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Rotation: {Math.round(selectedElement.rotation)}°
                </label>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={selectedElement.rotation}
                  onChange={(e) => updateElementProperty('rotation', Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Color
                </label>
                <input
                  type="color"
                  value={selectedElement.color}
                  onChange={(e) => updateElementProperty('color', e.target.value)}
                  className="w-full h-8 rounded cursor-pointer"
                />
              </div>

              {/* Element-specific properties */}
              {selectedElement.type === 'table' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Table Number
                    </label>
                    <input
                      type="number"
                      value={(selectedElement as TableElement).properties.tableNumber || 1}
                      onChange={(e) => updateElementProperty('properties', { tableNumber: Number(e.target.value) })}
                      className="w-full px-2 py-1 bg-white/60 border border-white/40 text-gray-800 rounded text-sm"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Capacity
                    </label>
                    <input
                      type="number"
                      value={(selectedElement as TableElement).properties.capacity || 8}
                      onChange={(e) => updateElementProperty('properties', { capacity: Number(e.target.value) })}
                      className="w-full px-2 py-1 bg-white/60 border border-white/40 text-gray-800 rounded text-sm"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Shape
                    </label>
                    <select
                      value={(selectedElement as TableElement).properties.shape || 'round'}
                      onChange={(e) => updateElementProperty('properties', { shape: e.target.value })}
                      className="w-full px-2 py-1 bg-white/60 border border-white/40 text-gray-800 rounded text-sm"
                    >
                      <option value="round">Round</option>
                      <option value="rectangular">Rectangular</option>
                      <option value="square">Square</option>
                    </select>
                  </div>
                </div>
              )}

              {selectedElement.type === 'buffet' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Station Name
                    </label>
                    <input
                      type="text"
                      value={selectedElement.properties.name || 'Buffet Station'}
                      onChange={(e) => updateElementProperty('properties', { name: e.target.value })}
                      className="w-full px-2 py-1 bg-white/60 border border-white/40 text-gray-800 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Station Type
                    </label>
                    <select
                      value={selectedElement.properties.type || 'Main Course'}
                      onChange={(e) => updateElementProperty('properties', { type: e.target.value })}
                      className="w-full px-2 py-1 bg-white/60 border border-white/40 text-gray-800 rounded text-sm"
                    >
                      <option value="Main Course">Main Course</option>
                      <option value="Appetizers">Appetizers</option>
                      <option value="Desserts">Desserts</option>
                      <option value="Drinks">Drinks</option>
                      <option value="Salads">Salads</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              )}

              {selectedElement.type === 'text_label' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Text Content
                    </label>
                    <input
                      type="text"
                      value={selectedElement.text || 'Text Label'}
                      onChange={(e) => updateElementProperty('text', e.target.value)}
                      className="w-full px-2 py-1 bg-white/60 border border-white/40 text-gray-800 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Font Size
                    </label>
                    <input
                      type="range"
                      min="8"
                      max="72"
                      value={selectedElement.properties.fontSize || 16}
                      onChange={(e) => updateElementProperty('properties', { fontSize: Number(e.target.value) })}
                      className="w-full"
                    />
                    <span className="text-xs text-gray-500">{selectedElement.properties.fontSize || 16}px</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Font Family
                    </label>
                    <select
                      value={selectedElement.properties.fontFamily || 'Arial'}
                      onChange={(e) => updateElementProperty('properties', { fontFamily: e.target.value })}
                      className="w-full px-2 py-1 bg-white/60 border border-white/40 text-gray-800 rounded text-sm"
                    >
                      <option value="Arial">Arial</option>
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Helvetica">Helvetica</option>
                      <option value="Georgia">Georgia</option>
                      <option value="Verdana">Verdana</option>
                    </select>
                  </div>
                </div>
              )}

              <button
                onClick={deleteElement}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Element</span>
              </button>

              <div className="text-xs text-gray-500 space-y-1">
                <p>• Click and drag to move elements</p>
                <p>• Use transform handles to resize</p>
                <p>• Double-click text labels to edit</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Select an element to edit its properties</p>
            </div>
          )}
        </div>
      </div>

      {/* Text Editing Modal */}
      {editingText && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="card-pastel rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">Edit Text</h3>
            <input
              type="text"
              value={editingText.text}
              onChange={(e) => setEditingText({ ...editingText, text: e.target.value })}
              onKeyDown={handleTextEditKeyPress}
              className="input-pastel"
              placeholder="Enter text..."
              autoFocus
            />
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={cancelTextEdit}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={saveTextEdit}
                className="px-4 py-2 btn-pastel text-white rounded-md"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
